/**
 * ตามเบิก — "is this line done?", in one place.
 *
 * ⚠️ THIS FILE EXISTS BECAUSE THE SAME QUESTION WAS ANSWERED TWO DIFFERENT WAYS AND BOTH
 * WERE WRONG, IN OPPOSITE DIRECTIONS.
 *
 *   The run LIST asked `run.status`, so a run that committed 1 of 11 rows rendered a green
 *   บันทึกแล้ว. Measured 2026-09-15: 65 of 78 runs carry money nobody has accounted for, and
 *   the screen calls 47 of them finished.
 *
 *   The run PAGE asked `link_state === 'linked'`, which is empty on all 124 rows committed
 *   through the old batch path — so a run that really did write its money read
 *   `ผูกแล้ว 0 / 8 · ยังไม่ผูก ฿12,358` and overstated the backlog by what it had already paid.
 *
 * Both screens now call isRowDone(). One rule, one file.
 *
 * ⚠️ THE RULE IS A UNION, AND THE SECOND HALF IS NOT LEGACY CRUFT. `write_confirmed` means
 * api 628 read the billing row back AFTER the write and saw `received`. That is a stronger
 * statement about money than `link_state`, which is only an assertion that a person pressed
 * a button. A row carrying write_confirmed moved real money and must never be offered as
 * work still to do — LinkControl already reaches the same conclusion its own way
 * (`undoOnly`), which is why บันทึกแล้ว rows offer ยกเลิก rather than ผูก.
 *
 * Measured on the live corpus, 2026-09-15 — 466 rows over 78 runs:
 *   done  124 rows · ฿215,645.21      (write_confirmed; the 2 link_state rows are inside it)
 *   open  342 rows · ฿532,108.99      of which 263 rows · ฿403,736.21 are ready to link now
 */

/** A file row that a person or a confirmed write has already settled. */
export function isRowDone(row) {
  return String(row?.link_state || '') === 'linked' || row?.write_confirmed === true
}

/** ไม่ต้องผูก — checked, deliberately never linkable. Counts as done, outside the money. */
export function isRowDismissed(row) {
  return String(row?.link_state || '') === 'dismissed'
}

/**
 * Rows that could never be linked, so they must leave the DENOMINATOR — otherwise the
 * counter can never close and people learn to ignore it.
 * ⚠️ They do NOT leave the baht. Prudential marks a row not_payable when one cell
 * (Coversheet) is blank — an emptiness test, not a statement about money — while that row
 * can still carry a real Payable Amt. "26/26" alone is a lie; "26/26 · ยังไม่ผูก ฿8,940" is not.
 */
export function isRowOutOfScope(row) {
  return row?.kind === 'crm_leftover' || row?.outcome === 'not_payable'
}

/** Still owed a decision. */
export function isRowOpen(row) {
  return !isRowDone(row) && !isRowDismissed(row) && !isRowOutOfScope(row)
}

const baht = (r) => Number(r?.file_amount_paid) || 0
const sum = (rs) => rs.reduce((t, r) => t + baht(r), 0)

/**
 * Level A from the rows themselves. Used by the run page, which already holds every row.
 * The identity that must hold, and that the screen must print:
 *     ยอดในไฟล์ = ผูกแล้ว + ไม่ต้องผูก + ยังไม่ผูก
 */
export function runProgress(rows = []) {
  const inFile = rows.filter((r) => r?.kind !== 'crm_leftover')
  const done = inFile.filter(isRowDone)
  const dismissed = inFile.filter((r) => !isRowDone(r) && isRowDismissed(r))
  const outside = inFile.filter((r) => !isRowDone(r) && !isRowDismissed(r) && isRowOutOfScope(r))
  const open = inFile.filter(isRowOpen)
  return {
    total: done.length + open.length,          // the denominator excludes dismissed + not-payable
    done: done.length,
    open: open.length,
    dismissed: dismissed.length,
    outside: outside.length,
    doneBaht: sum(done),
    openBaht: sum(open),
    outsideBaht: sum(dismissed) + sum(outside), // shown separately, never hidden
    fileBaht: sum(inFile),
  }
}

/**
 * FALLBACK ONLY — Level A from the run's own counters, used when the row marks could not
 * be loaded. Callers want runOpen(), never this directly.
 *
 * ⚠️ THIS WAS THE LIST'S RULE, AND IT WENT WRONG THE DAY ผูก STARTED BEING USED. The claim
 * below was true when it was measured on 2026-09-15, because every done row then was a
 * write_confirmed row. `rows_written` / `amount_written` are moved ONLY by the old batch
 * commit; a row closed by ผูก — above all `already_received`, which writes no money — never
 * touches them. By 2026-09-17 run #88 read 8 / 8 on its own page and 1 / 8 · ฿9,608.04 on
 * the list, and 7 of 83 runs disagreed. api 661 now hands the list the row marks.
 *
 * (Original, now historical:) `amount_parsed` is the sum of every row's file figure and
 * `amount_written` is the sum of the confirmed ones, so their difference IS the open money.
 * Checked against the rows themselves on 2026-09-15: ฿532,108.99 from both sides.
 *
 * ⚠️ REVERTED RUNS ARE TREATED AS HAVING WRITTEN NOTHING. commit.js counted a revert as
 * `rows_written` until 2026-09-14, so historic rolled-back runs still carry the old number.
 * The totals strip on this page already excluded them for exactly this reason; the same
 * correction has to apply per row or run #1 reads "4 saved" while its ฿5,700 is still open.
 */
const REVERTED_RUN = new Set(['reverted', 'partially_reverted'])

export function runOpenFromCounters(run) {
  if (!run) return { open: 0, done: 0, total: 0, openBaht: 0, known: false }
  const reverted = REVERTED_RUN.has(run.status)
  const total = Number(run.rows_total) || 0
  const done = reverted ? 0 : Number(run.rows_written) || 0
  const parsed = Number(run.amount_parsed) || 0
  const written = reverted ? 0 : Number(run.amount_written) || 0
  return {
    total,
    done,
    open: Math.max(0, total - done),
    doneBaht: written,
    openBaht: Math.max(0, parsed - written),
    // A run that never parsed has nothing to say about progress; the caller shows a dash.
    known: run.status !== 'parse_failed' && run.status !== 'rejected' && total > 0,
    fromRows: false,
  }
}

/**
 * Level A for the run LIST — the SAME runProgress() the run page's tile calls, over the
 * row marks `functions/api/claim-import/runs.js` attaches as `run.progress_rows`.
 * Falls back to the counters only when those marks are absent (the proxy says why in
 * `progress_error`, and the list prints it — a silent fallback is how #88 went unnoticed).
 */
export function runOpen(run) {
  if (!run) return runOpenFromCounters(run)
  if (!Array.isArray(run.progress_rows)) return runOpenFromCounters(run)
  const p = runProgress(run.progress_rows)
  const inFile = p.done + p.open + p.dismissed + p.outside
  return {
    total: p.total,
    done: p.done,
    open: p.open,
    doneBaht: p.doneBaht,
    openBaht: p.openBaht,
    known: run.status !== 'parse_failed' && run.status !== 'rejected' && inFile > 0,
    fromRows: true,
  }
}

/**
 * Does this run carry ANY link record — linked, dismissed, or released (unlinked)?
 * Each of those has events in claim_import_link_event pointing at these rows, so deleting
 * the run would orphan the audit trail. `null` when the marks are absent: unknown, and the
 * caller must treat unknown as "yes".
 */
export function runHasLinkRecords(run) {
  if (!Array.isArray(run?.progress_rows)) return null
  return run.progress_rows.some((r) => String(r?.link_state || '') !== '' || r?.write_confirmed === true)
}

/**
 * What the run list should SAY, derived from the rows rather than from `run.status`.
 * ⚠️ `committed` is deliberately not a value here. It is the word that let 47 runs read as
 * finished while holding ฿532,108.99 between them.
 */
export function runPhase(run) {
  if (run?.status === 'parse_failed') return 'parse_failed'
  if (run?.status === 'rejected') return 'rejected'
  const p = runOpen(run)
  if (!p.known) return 'empty'
  return p.open > 0 ? 'open' : 'done'
}

export const RUN_PHASE = {
  open: { label: 'ยังไม่ครบ', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  done: { label: 'ครบแล้ว', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  parse_failed: { label: 'อ่านไฟล์ไม่ได้', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  rejected: { label: 'ไม่ผ่านการตรวจ', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  empty: { label: 'ไม่มีรายการ', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
}
